import { useEffect, useState } from "react";
import { Check, Save, Settings } from "lucide-react";
import { toast } from "sonner";
import { NETWORKS } from "@/data/networks";
import { applySettings } from "@/lib/settings";

const API = process.env.REACT_APP_BACKEND_URL;

const Input = ({ label, value, onChange, placeholder, hint, testId, type = "text" }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">{label}</span>
    <input
      type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      data-testid={testId}
      className="mt-2 w-full h-11 rounded-full border border-[#E8DDD3] bg-[#FBF8F4] px-4 text-sm text-[#1F1A17] outline-none focus:border-[#1F1A17] transition-colors placeholder:text-[#B5ABA3]"
    />
    {hint && <span className="mt-1 block text-[11px] text-[#685F58]">{hint}</span>}
  </label>
);

const fromServer = (s) => ({
  social: s.social || {}, youtubeId: s.youtubeId || "", digestTo: s.digestTo || "",
  commissionPct: s.commissionPct ?? 25, commissionMonths: s.commissionMonths ?? 12, bonusAmount: s.bonusAmount ?? 100, bonusThreshold: s.bonusThreshold ?? 25,
});

export const SettingsPanel = ({ pw }) => {
  const [form, setForm] = useState({ social: {}, youtubeId: "", digestTo: "", commissionPct: 25, commissionMonths: 12, bonusAmount: 100, bonusThreshold: 25 });
  const [fallback, setFallback] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/api/settings/all`, { headers: { "X-Stats-Key": pw } })
      .then((r) => r.json())
      .then((s) => { setForm(fromServer(s)); setFallback(s.digestFallback || ""); });
  }, [pw]);

  const setSocial = (k, v) => setForm((f) => ({ ...f, social: { ...f.social, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    const r = await fetch(`${API}/api/settings`, {
      method: "PUT", headers: { "Content-Type": "application/json", "X-Stats-Key": pw }, body: JSON.stringify({ ...form, origin: window.location.origin }),
    });
    const body = await r.json().catch(() => ({}));
    setSaving(false);
    if (!r.ok) {
      const msg = Array.isArray(body.detail) ? body.detail.map((d) => d.msg.replace("Value error, ", "")).join(" · ") : body.detail || "Could not save.";
      setError(msg); return;
    }
    setForm(fromServer(body));
    applySettings(body);
    toast.success("Settings saved — live on every page");
  };

  const filled = NETWORKS.filter((n) => form.social[n.key]).length;

  return (
    <section className="mt-12 rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="stats-settings">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-xl grid place-items-center bg-[#1F1A17] text-[#FBF8F4]"><Settings className="h-4 w-4" /></span>
          <div>
            <h2 className="font-serif text-2xl tracking-tight text-[#1F1A17]">Settings</h2>
            <p className="text-xs text-[#685F58]">Real links and the tour video go live on every page the moment you save. {filled}/4 profile links set.</p>
          </div>
        </div>
      </div>

      <form onSubmit={save} className="mt-6 grid md:grid-cols-2 gap-5">
        {NETWORKS.map((n) => (
          <Input key={n.key} label={`${n.name} page URL`} value={form.social[n.key] || ""} onChange={(v) => setSocial(n.key, v)}
            placeholder={`https://www.${n.key}.com/hearthvault`} testId={`settings-social-${n.key}`} />
        ))}
        <Input label="Tour video (YouTube link or ID)" value={form.youtubeId} onChange={(v) => setForm((f) => ({ ...f, youtubeId: v }))}
          placeholder="https://youtu.be/XXXXXXXXXXX" hint="Paste any YouTube link — the 11-character ID is extracted and saved." testId="settings-youtube" />
        <Input label="Weekly digest email" type="email" value={form.digestTo} onChange={(v) => setForm((f) => ({ ...f, digestTo: v }))}
          placeholder="you@hearthvault.family" hint={fallback ? `Falls back to ${fallback} from the server config if empty.` : "Monday 9:00 UTC. Leave empty to pause the digest."} testId="settings-digest" />
        <div className="md:col-span-2 grid grid-cols-2 lg:grid-cols-4 gap-5 rounded-2xl bg-[#FBF8F4] border border-[#E8DDD3] p-5" data-testid="settings-partner-terms">
          <div className="col-span-2 lg:col-span-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B4532A]">Partner terms (shown on /partners)</div>
          <Input label="Commission %" type="number" value={form.commissionPct} onChange={(v) => setForm((f) => ({ ...f, commissionPct: Number(v) }))} testId="settings-commission-pct" />
          <Input label="For (months)" type="number" value={form.commissionMonths} onChange={(v) => setForm((f) => ({ ...f, commissionMonths: Number(v) }))} testId="settings-commission-months" />
          <Input label="Bonus ($)" type="number" value={form.bonusAmount} onChange={(v) => setForm((f) => ({ ...f, bonusAmount: Number(v) }))} testId="settings-bonus-amount" />
          <Input label="Bonus at (active families)" type="number" value={form.bonusThreshold} onChange={(v) => setForm((f) => ({ ...f, bonusThreshold: Number(v) }))} testId="settings-bonus-threshold" />
        </div>
        <div className="md:col-span-2 flex flex-wrap items-center gap-4">
          <button type="submit" disabled={saving} data-testid="settings-save" className="inline-flex items-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] disabled:opacity-60 h-11 px-5 text-sm font-medium text-[#FBF8F4] transition-colors duration-300">
            {saving ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />} {saving ? "Saving…" : "Save settings"}
          </button>
          {error && <p className="text-sm text-[#B4532A]" data-testid="settings-error">{error}</p>}
        </div>
      </form>
    </section>
  );
};
